import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { removeArrayItem, updateArray, addICDArrayItem, removeICDArrayItem, updateICDArray } from './dataActions';
import CPTRecommendation from './CPTRecommendation';
import ModifierRecommendation from './ModifierRecommendation';
import { Box, Button, HStack, Input, SimpleGrid, Text, VStack } from '@chakra-ui/react';

import { CSS } from '@dnd-kit/utilities';
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import RenderDynamicICD from './RenderDynamicICD';
import { restrictToParentElement } from '@dnd-kit/modifiers';

const RenderDynamicCPT = ({
    id,
    cptIndex,
    cpt,
    label,

    cptArray,
    icdArray,
    modifierArray,
    unitArray,
    setCPTs,
    setICDs,
    setModifiers,
    setUnits,

    industryCodes,
    iCDCodes,
    modifiers,
    agree,
    icdAgree,
    formSchema,
    errorKey,
    formErrors,
    setFormErrors,
    refsForCPT,
    refsForICD,
    refsForModifier,
    visibleModifiers,
    setVisibleModifiers,
    cptDisabled,
    icdDisabled,

    auditedCPTs,
    auditedICDs,
    auditedModifiers,
    auditedUnits,
    setAuditedCPTs,
    setAuditedICDs,
    setAuditedModifiers,
    setAuditedUnits,
    refsForAuditedCPT,
    refsForAuditedICD,
    refsForAuditedModifier,
    setVisibleAuditedModifiers,
}) => {

    const sortableId = id?.toString?.() ?? "";
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: sortableId, animateLayoutChanges: () => false });

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 1,
            },
        })
    );

    const style = {
        transform: transform ? CSS.Transform.toString(transform) : undefined,
        transition,
        marginBottom: '0rem',
        height: 'auto',
        opacity: isDragging ? 0.5 : undefined,
        boxShadow: isDragging ? '0 4px 10px rgba(0, 0, 0, 0.2)' : undefined,
    };

    const handleICDDragEnd = (event, cptIndex) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = parseInt(active.id);
        const newIndex = parseInt(over.id);

        const updated = [...icdArray[cptIndex]];
        const reordered = arrayMove(updated, oldIndex, newIndex);

        setICDs(prev => {
            const copy = [...prev];
            copy[cptIndex] = reordered;
            return copy;
        });

        if (label === 'billed' && icdAgree) {
            setAuditedICDs(prev => {
                const copy = [...prev];
                copy[cptIndex] = arrayMove(copy[cptIndex], oldIndex, newIndex);
                return copy;
            });
        }
    };

    const isCPTDragDisabled = label === "audited" && agree;
    const isICDDragDisabled = label === "audited" && icdAgree;

    return (
        <div ref={setNodeRef} style={style}>
            <Box position="relative" key={cptIndex} w="full" >
                {/* Row Number */}
                <Text
                    {...(!isCPTDragDisabled ? listeners : {})}
                    {...(!isCPTDragDisabled ? attributes : {})}
                    cursor={isCPTDragDisabled ? 'default' : (isDragging ? 'grabbing' : 'grab')}
                    position="absolute"
                    top={-1}
                    left={-3}
                    fontSize="sm"
                    fontWeight="bold"
                    color="teal.600"
                    backgroundColor={isDragging ? 'gray.100' : 'white'}
                    p={1}
                    borderRadius="full"
                    border="1px solid #ccc"
                    boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                    _hover={{
                        backgroundColor: isCPTDragDisabled ? 'white' : 'gray.200',
                    }}
                >
                    {cptIndex + 1}
                </Text>
                <Button
                    size="xs"
                    colorScheme="red"
                    position="absolute"
                    top={0}
                    right={-7}
                    onClick={() => {
                        removeArrayItem(setCPTs, cptArray, cptIndex, refsForCPT);
                        removeArrayItem(setModifiers, modifierArray, cptIndex, refsForModifier);
                        removeArrayItem(setUnits, unitArray, cptIndex);
                        removeArrayItem(setICDs, icdArray, cptIndex, refsForICD);
                        if (label === "billed" && agree) {
                            removeArrayItem(setAuditedCPTs, auditedCPTs, cptIndex, refsForAuditedCPT);
                            removeArrayItem(setAuditedICDs, auditedICDs, cptIndex, refsForAuditedModifier);
                            removeArrayItem(setAuditedModifiers, auditedModifiers, cptIndex);
                            removeArrayItem(setAuditedUnits, auditedUnits, cptIndex);
                        }
                        setFormErrors((prevErrors) => {
                            const updatedErrors = { ...prevErrors };

                            if (updatedErrors[errorKey]) {
                                const newErrors = { ...updatedErrors[errorKey] };
                                delete newErrors[cptIndex];
                                updatedErrors[errorKey] = newErrors;
                            }

                            if (label === "billed" && agree && updatedErrors.auditedCPTs) {
                                const newErrorsAudited = { ...updatedErrors.auditedCPTs };

                                if (newErrorsAudited?.[cptIndex]) {
                                    delete newErrorsAudited[cptIndex];
                                    updatedErrors.auditedCPTs = newErrorsAudited;
                                }
                            }

                            return updatedErrors;
                        });
                    }}
                    isDisabled={cptDisabled || icdDisabled}
                >
                    -
                </Button>

                <VStack align="start" spacing={4} w="full" pb={1} border="1px solid #ccc" p={3} borderRadius="md">
                    <HStack spacing={2} w="full">
                        {/* CPT Field */}
                        <Box flex={0.8}>
                            <CPTRecommendation
                                industryCodes={industryCodes}
                                index={cptIndex}
                                value={cpt || ""}
                                cptArray={cptArray}
                                isDisabled={cptDisabled}
                                setFormErrors={setFormErrors}
                                formSchema={formSchema}
                                formErrors={formErrors}
                                errorKey={errorKey}
                                label={label}
                                agree={agree}
                                onSelect={(selectedKey) => {
                                    const [selectedCPT, selectedModifier] = selectedKey.split("-");

                                    // Duplicate check (ignore empty strings)
                                    const isDuplicate =
                                        selectedCPT &&
                                        cptArray.some((existing, idx) => {
                                            const [existingCPT] = existing.split("-");
                                            return idx !== cptIndex && existingCPT === selectedCPT;
                                        });

                                    if (isDuplicate) {
                                        // Set your form error for this row/index
                                        setFormErrors(prev => ({
                                            ...prev,
                                            [errorKey]: {
                                                ...(prev[errorKey] || {}),
                                                [cptIndex]: "CPT already exists",
                                            },
                                        }));
                                        return;
                                    }

                                    // Clear any previous duplicate error for this row
                                    setFormErrors(prev => ({
                                        ...prev,
                                        [errorKey]: {
                                            ...(prev[errorKey] || {}),
                                            [cptIndex]: undefined,
                                        },
                                    }));

                                    // Proceed with your updates
                                    updateArray(setCPTs, cptArray, cptIndex, selectedCPT);
                                    updateArray(setModifiers, modifierArray, cptIndex, {
                                        primary: selectedModifier || modifierArray[cptIndex]?.primary || "",
                                        secondary1: modifierArray[cptIndex]?.secondary1 || "",
                                        secondary2: modifierArray[cptIndex]?.secondary2 || "",
                                    });

                                    if (label === "billed" && agree) {
                                        updateArray(setAuditedCPTs, auditedCPTs, cptIndex, selectedCPT);
                                        updateArray(setAuditedModifiers, auditedModifiers, cptIndex, {
                                            primary: selectedModifier || modifierArray[cptIndex]?.primary || "",
                                            secondary1: modifierArray[cptIndex]?.secondary1 || "",
                                            secondary2: modifierArray[cptIndex]?.secondary2 || "",
                                        });
                                    }
                                }}
                                inputRef={(ref) => {
                                    refsForCPT.current[cptIndex] = ref;
                                }}
                            />
                        </Box>

                        {/* Unit Field */}
                        <Box flex={0.4}>
                            <Input
                                type="number"
                                placeholder="U"
                                min={1}
                                value={unitArray[cptIndex] === undefined ? 1 : unitArray[cptIndex]}
                                isDisabled={cptDisabled}
                                onWheel={(e) => e.target.blur()}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const parsed = parseInt(value, 10);

                                    const finalValue = value === "" ? "" : Math.max(1, Math.min(parsed, 99));

                                    updateArray(setUnits, unitArray, cptIndex, finalValue);

                                    if (label === "billed" && agree) {
                                        updateArray(setAuditedUnits, auditedUnits, cptIndex, finalValue);
                                    }
                                }}
                            />
                        </Box>

                        {/* Modifier Section */}
                        <Box flex={1.8} border="1px dashed #bbb" p={2} borderRadius="md">
                            <HStack spacing={2} w="full">
                                <ModifierRecommendation
                                    modifierCodes={modifiers}
                                    index={0}
                                    value={modifierArray[cptIndex]?.primary || ""}
                                    isDisabled={cptDisabled}
                                    onSelect={(selectedModifier) => {
                                        updateArray(setModifiers, modifierArray, cptIndex, { primary: selectedModifier });
                                        if (label === "billed" && agree) {
                                            updateArray(setAuditedModifiers, auditedModifiers, cptIndex, { primary: selectedModifier });
                                        }
                                    }}
                                    inputRef={(ref) => {
                                        if (!refsForModifier.current[cptIndex]) {
                                            refsForModifier.current[cptIndex] = [];
                                        }
                                        refsForModifier.current[cptIndex][0] = ref;
                                    }}
                                />

                                {/* Secondary Modifiers */}
                                {visibleModifiers[cptIndex] >= 2 && (
                                    <ModifierRecommendation
                                        modifierCodes={modifiers}
                                        index={1}
                                        value={modifierArray[cptIndex]?.secondary1 || ""}
                                        isDisabled={cptDisabled}
                                        onSelect={(selectedModifier) => {
                                            updateArray(setModifiers, modifierArray, cptIndex, {
                                                ...modifierArray[cptIndex],
                                                secondary1: selectedModifier,
                                            })
                                            if (label === "billed" && agree) {
                                                updateArray(setAuditedModifiers, auditedModifiers, cptIndex, {
                                                    ...auditedModifiers[cptIndex],
                                                    secondary1: selectedModifier,
                                                })
                                            }
                                        }}
                                        inputRef={(ref) => {
                                            if (!refsForModifier.current[cptIndex]) {
                                                refsForModifier.current[cptIndex] = [];
                                            }
                                            refsForModifier.current[cptIndex][1] = ref;
                                        }}
                                    />
                                )}
                                {visibleModifiers[cptIndex] >= 3 && (
                                    <ModifierRecommendation
                                        modifierCodes={modifiers}
                                        index={2}
                                        value={modifierArray[cptIndex]?.secondary2 || ""}
                                        isDisabled={cptDisabled}
                                        onSelect={(selectedModifier) => {
                                            updateArray(setModifiers, modifierArray, cptIndex, {
                                                ...modifierArray[cptIndex],
                                                secondary2: selectedModifier,
                                            })
                                            if (label === "billed" && agree) {
                                                updateArray(setAuditedModifiers, auditedModifiers, cptIndex, {
                                                    ...auditedModifiers[cptIndex],
                                                    secondary2: selectedModifier,
                                                })
                                            }
                                        }}
                                        inputRef={(ref) => {
                                            if (!refsForModifier.current[cptIndex]) {
                                                refsForModifier.current[cptIndex] = [];
                                            }
                                            refsForModifier.current[cptIndex][2] = ref;
                                        }}
                                    />
                                )}

                                {/* Modifier Controls */}
                                <HStack>
                                    {visibleModifiers[cptIndex] > 1 && (
                                        <Button
                                            size="xs"
                                            colorScheme="gray"
                                            isDisabled={cptDisabled}
                                            onClick={() => {
                                                setVisibleModifiers((prev) => {
                                                    const newModifiers = [...prev];
                                                    newModifiers[cptIndex] = Math.max(1, newModifiers[cptIndex] - 1);
                                                    return newModifiers;
                                                });
                                                if (label === "billed" && agree) {
                                                    setVisibleAuditedModifiers((prev) => {
                                                        const newModifiers = [...prev];
                                                        newModifiers[cptIndex] = Math.max(1, newModifiers[cptIndex] - 1);
                                                        return newModifiers;
                                                    });
                                                }

                                                if (visibleModifiers[cptIndex] === 2) {
                                                    updateArray(setModifiers, modifierArray, cptIndex, {
                                                        ...modifierArray[cptIndex],
                                                        secondary1: "",
                                                    });
                                                    if (label === "billed" && agree) {
                                                        updateArray(setAuditedModifiers, auditedModifiers, cptIndex, {
                                                            ...auditedModifiers[cptIndex],
                                                            secondary1: "",
                                                        })
                                                    }

                                                } else if (visibleModifiers[cptIndex] === 3) {
                                                    updateArray(setModifiers, modifierArray, cptIndex, {
                                                        ...modifierArray[cptIndex],
                                                        secondary2: "",
                                                    });
                                                    if (label === "billed" && agree) {
                                                        updateArray(setAuditedModifiers, auditedModifiers, cptIndex, {
                                                            ...auditedModifiers[cptIndex],
                                                            secondary2: "",
                                                        })
                                                    }
                                                }
                                            }}
                                        >
                                            <Text color="red.500" fontWeight="bold">-</Text>
                                        </Button>
                                    )}
                                    {(visibleModifiers[cptIndex] === undefined || visibleModifiers[cptIndex] < 3) && (
                                        <Button
                                            size="xs"
                                            colorScheme="gray"
                                            isDisabled={cptDisabled}
                                            onClick={() => {
                                                setVisibleModifiers((prev) => {
                                                    const newModifiers = [...prev];
                                                    const current = newModifiers[cptIndex] ?? 1;
                                                    newModifiers[cptIndex] = Math.min(3, current + 1);
                                                    return newModifiers;
                                                });

                                                if (label === "billed" && agree) {
                                                    setVisibleAuditedModifiers((prev) => {
                                                        const newModifiers = [...prev];
                                                        const current = newModifiers[cptIndex] ?? 1;
                                                        newModifiers[cptIndex] = Math.min(3, current + 1);
                                                        return newModifiers;
                                                    });
                                                }

                                                setTimeout(() => {
                                                    const nextIndex = (visibleModifiers[cptIndex] ?? 1) + 1;
                                                    if (
                                                        refsForModifier.current[cptIndex] &&
                                                        refsForModifier.current[cptIndex][nextIndex]
                                                    ) {
                                                        refsForModifier.current[cptIndex][nextIndex].focus();
                                                    }
                                                }, 0);
                                            }}
                                        >
                                            +
                                        </Button>
                                    )}
                                </HStack>
                            </HStack>
                        </Box>
                    </HStack>

                    <Box lineHeight="normal">
                        {formErrors?.[errorKey]?.[cptIndex] && (
                            <Text color="red.500" fontSize="sm" mt="-18%" mb="-4" p="0">
                                {formErrors[errorKey][cptIndex]}
                            </Text>
                        )}
                    </Box>

                    <Box w="full" p={3} border="1px solid #ccc" borderRadius="md" backgroundColor="#f9f9f9" sx={{ mt: "0 !important" }}>
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleICDDragEnd(event, cptIndex)}
                            modifiers={[restrictToParentElement]}
                        >
                            <SortableContext
                                items={Array.isArray(icdArray[cptIndex]) ? icdArray[cptIndex].map((_, icdIndex) => icdIndex.toString()) : []}
                                strategy={rectSortingStrategy}
                            >
                                <SimpleGrid columns={3} spacing={2} w="full">
                                    {(Array.isArray(icdArray[cptIndex]) ? icdArray[cptIndex] : []).map((icd, icdIndex) => (
                                        <RenderDynamicICD
                                            key={icdIndex}
                                            id={icdIndex}
                                            icdIndex={icdIndex}
                                            icd={icd}
                                            icdCodes={iCDCodes}
                                            isDisabled={icdDisabled}
                                            isICDDragDisabled={isICDDragDisabled}
                                            onSelect={(selectedICD) => {
                                                updateICDArray(setICDs, icdArray, cptIndex, icdIndex, selectedICD);
                                                if (label === "billed" && icdAgree) {
                                                    updateICDArray(setAuditedICDs, auditedICDs, cptIndex, icdIndex, selectedICD);
                                                }
                                            }}
                                            inputRef={(ref) => {
                                                if (!refsForICD.current[cptIndex]) {
                                                    refsForICD.current[cptIndex] = [];
                                                }
                                                refsForICD.current[cptIndex][icdIndex] = ref;
                                            }}
                                            onRemove={() => {
                                                removeICDArrayItem(setICDs, icdArray, cptIndex, icdIndex, refsForICD)
                                                if (label === "billed" && agree) {
                                                    removeICDArrayItem(setAuditedICDs, auditedICDs, cptIndex, icdIndex, refsForAuditedICD)
                                                }
                                            }}
                                        />
                                    ))}

                                </SimpleGrid>
                            </SortableContext>
                        </DndContext>

                        {/* Add ICD Button */}
                        <Button
                            mt={2}
                            size="xs"
                            colorScheme="gray"
                            isDisabled={icdDisabled}
                            onClick={() => addICDArrayItem(setICDs, icdArray, cptIndex, refsForICD)}
                        >
                            + ICD
                        </Button>
                    </Box>
                </VStack >
            </Box >
        </div>
    );
};

export default RenderDynamicCPT;